import { ParsedCSVData } from '@/types/upload';

/**
 * CSV バイト列を文字列にデコードする（文字コード自動判定）。
 *
 * サーバー側（ProcessUpload / S3Handler.detect_encoding）と同じ優先順位で判定する:
 *   1. UTF-8（BOM 有無どちらも。TextDecoder が先頭 BOM を自動除去）
 *   2. Shift_JIS（UTF-8 として不正なら試行）
 *   3. どちらでも不正なら UTF-8 ロッシー（サーバーの最終フォールバックと同じ）
 *
 * ブラウザの File.text() は UTF-8 固定のため、Shift_JIS の CSV が文字化けして
 * プレビュー検証が誤判定する。本関数で ArrayBuffer から判定してサーバーと挙動を揃える。
 */
export const decodeCsvBytes = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    try {
      return new TextDecoder('shift_jis', { fatal: true }).decode(bytes);
    } catch {
      // 最終フォールバック（サーバーと同じく UTF-8 ロッシー）
      return new TextDecoder('utf-8').decode(bytes);
    }
  }
};

const parseLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

export const parseCSV = (text: string): ParsedCSVData => {
  // UTF-8 BOM (U+FEFF) を除去（Excel 等が付与する先頭マーカー。除去しないと 1 列目の
  // ヘッダーが BOM 付きになりヘッダー完全一致バリデーションで誤判定するのを防ぐ）。
  const lines = text
    .replace(/^\uFEFF/, '')
    .split('\n')
    .filter((line) => line.trim());

  // 1行目: 英語ヘッダー（フィールド名、バリデーション用）
  // 2行目: 日本語ラベル（プレビュー表示用）
  // 空ファイル / 1 行のみのファイルでも例外にせず、後段のヘッダー検証で
  // 「ヘッダーが不正」「データ行がありません」として扱わせる。
  const [headerLine, displayLine] = lines;
  const headers = headerLine === undefined ? [] : parseLine(headerLine);
  const displayHeaders = displayLine === undefined ? headers : parseLine(displayLine);

  // 3行目以降: データ
  const rows = lines.slice(2).map(parseLine);

  return { headers, displayHeaders, rows };
};
